/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useState } from 'react';
import {
  Checkbox,
  Toggle,
  Table,
  TableRow,
  TableBody,
  Tag,
  TableCell,
  TableContainer,
  PaginationNav,
  ProgressBar,
  TextInput
} from '@carbon/react';

import * as Styled from './TablesView.styled';

/** @typedef {import('../types').Table} Table */
/**
 * @param {{
 *   tables: Table[],
 *   onToggleActive: (tableId: string, active: boolean) => void,
 *   onToggleAllActive: (active: boolean) => void,
 *   onNameChange: (tableId: string, value: string) => void
 * }} props
 * @returns {JSX.Element}
 */
export const TablesView = ({ tables, onToggleActive, onToggleAllActive, onNameChange }) => {
  const [selectedTableIndex, setSelectedTableIndex] = useState(0);
  const [showConfidence, setShowConfidence] = useState(true);

  const currentTable = tables[selectedTableIndex];
  const tableRows = Array.isArray(currentTable?.value) ? currentTable.value : [];
  const confidenceMatrix = Array.isArray(currentTable?.confidence) ? currentTable.confidence : [];
  const isCurrentTableSelected = currentTable?.active;
  const dataRows = tables;
  const allRowsActive = dataRows.length > 0 && dataRows.every((row) => row.active);
  const someRowsActive = dataRows.length > 0 && dataRows.some((row) => row.active) && !allRowsActive;

  const minConfidence = Math.min(...confidenceMatrix.flatMap((row) => row.filter((value) => value)));
  const avgConfidence = Math.round(
    confidenceMatrix
      .flatMap((row) => row.filter((value) => value !== undefined))
      .reduce((acc, value) => acc + value, 0) / confidenceMatrix.flat().filter((value) => value).length
  );

  return (
    <Styled.TableViewContainer>
      <Styled.HeaderRow>
        <Styled.HeaderLeft>
          <p>
            {tables.length} table{tables.length !== 1 ? 's' : ''} extracted
          </p>
          {tables.length > 0 && (
            <PaginationNav
              size="sm"
              itemsShown={4}
              page={selectedTableIndex}
              totalItems={tables.length}
              onChange={(page) => setSelectedTableIndex(page)}
            />
          )}
        </Styled.HeaderLeft>
        {tables.length > 0 && (
          <Styled.HeaderRight>
            <Checkbox
              id="select-all-tables-checkbox"
              labelText="Activate all tables"
              checked={allRowsActive}
              indeterminate={someRowsActive}
              onChange={(_e, { checked }) => onToggleAllActive(checked)}
              disabled={tables.length === 0}
            />
          </Styled.HeaderRight>
        )}
      </Styled.HeaderRow>
      <Styled.HeaderRow>
        {tables.length > 0 && (
          <Styled.HeaderLeft>
            <Checkbox
              labelText={
                <TextInput
                  id="table-name-input"
                  value={currentTable.replacedName ?? currentTable.name}
                  size="sm"
                  labelText="Field name"
                  hideLabel
                  onChange={(e) => onNameChange(currentTable.id, e.target.value)}
                  aria-label={`Edit field name for ${currentTable?.name}`}
                />
              }
              id={`table-${selectedTableIndex}-checkbox`}
              checked={isCurrentTableSelected}
              onChange={(_e, { checked }) => onToggleActive(currentTable.id, checked)}
            />
          </Styled.HeaderLeft>
        )}
        {tables.length > 0 && (
          <Styled.HeaderRight>
            <Toggle
              id="show-confidence-toggle"
              size="sm"
              toggled={showConfidence}
              labelText="Show confidence scores"
              hideLabel
              onToggle={setShowConfidence}
            />
            <ProgressBar
              value={minConfidence}
              max={100}
              label="Min confidence"
              status="active"
              helperText={minConfidence ? `${minConfidence}%` : 'N/A'}
              size="small"
            />
            <ProgressBar
              value={avgConfidence}
              max={100}
              label="Avg. confidence"
              status="active"
              helperText={avgConfidence ? `${avgConfidence}%` : 'N/A'}
              size="small"
            />
          </Styled.HeaderRight>
        )}
      </Styled.HeaderRow>
      <Styled.TableContent>
        <TableContainer>
          <Table size="md">
            <TableBody>
              {tableRows?.map((row, rowIndex) =>
                row?.cells ? (
                  <TableRow key={row.rowId}>
                    {row.cells.map((cell, colIdx) =>
                      cell ? (
                        <TableCell key={cell.cellId}>
                          <Styled.CellValue>{cell.value}</Styled.CellValue>
                          {showConfidence &&
                            confidenceMatrix[rowIndex] &&
                            confidenceMatrix[rowIndex][colIdx] !== undefined && (
                              <Tag type="blue">{`${confidenceMatrix[rowIndex][colIdx]}%`}</Tag>
                            )}
                        </TableCell>
                      ) : null
                    )}
                  </TableRow>
                ) : null
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Styled.TableContent>
    </Styled.TableViewContainer>
  );
};
